<script lang="ts">
  import type { Store } from '../state/store.svelte';
  import type { GraphicsProfile, BodyId } from '../state/selectors';
  import { selectIsAlternateCentre, selectCentreName, selectBodyView } from '../state/selectors';
  import type { ResolvedProfile } from '../render/resolve-profile';
  import TopBar from './TopBar.svelte';
  import CanvasHost from './CanvasHost.svelte';
  import SettingsPanel from './SettingsPanel.svelte';
  import BodyLegend from './BodyLegend.svelte';
  import DetailPanel from './DetailPanel.svelte';
  import ModelNotice from './ModelNotice.svelte';
  import PlaybackHud from './PlaybackHud.svelte';
  import LiveRegion from './LiveRegion.svelte';
  import StatusToast from './StatusToast.svelte';

  // The workspace shell: a full-bleed canvas with floating glass overlays. Story
  // 1.1 wires a real, keyboard-operable side-panel collapse toggle so the panels
  // collapse behind it rather than shrinking the canvas (usable at school-laptop
  // resolutions). Below ~1280px the panels start collapsed.
  //
  // `resolve` is the capability-bound effective-mode resolver (Story 1.6) threaded
  // to CanvasHost so it can rebuild the decoration overlay on a profile change or a
  // context-loss fallback; `ui` never imports `platform`. `osReducedMotion` is the
  // OS reduced-motion signal passed to the active renderer.
  let {
    store,
    resolve,
    osReducedMotion = false,
    startupNotice = '',
    buildShareUrl,
    copyText
  }: {
    store: Store;
    resolve: (profile: GraphicsProfile) => ResolvedProfile;
    osReducedMotion?: boolean;
    // Story 5.1 (AC-11): a one-shot, non-blocking invalid-shared-link notice raised
    // by the composition root when a fragment could not be decoded. Empty otherwise.
    startupNotice?: string;
    // Story 5.2: the URL/clipboard side-effect seams threaded from the composition
    // root (AD-2), so `ui` imports neither `share` nor `platform`. `buildShareUrl`
    // encodes the current state, writes the fragment (5.1 is the sole URL owner), and
    // returns the shareable href; `copyText` is the defensive clipboard adapter.
    buildShareUrl: () => string;
    copyText: (text: string) => Promise<boolean>;
  } = $props();

  const NARROW_BREAKPOINT = 1300;
  let panelsCollapsed = $state(
    typeof window !== 'undefined' ? window.innerWidth < NARROW_BREAKPOINT : false
  );
  let liveMessage = $state('');
  // A SEPARATE assertive live region (Story 4.2, EXPERIENCE.md L212): central
  // absorption announces here (role="alert"), so it interrupts, while every other
  // announcement stays on the polite region. Kept distinct so an absorption toast
  // never downgrades to polite and a routine value change never interrupts.
  let liveAssertive = $state('');
  let settingsOpen = $state(false);

  function togglePanels(): void {
    panelsCollapsed = !panelsCollapsed;
    liveMessage = panelsCollapsed ? 'Side panels hidden.' : 'Side panels shown.';
  }

  // The ARIA live region is owned here; DetailPanel announces value changes (e.g.
  // eccentricity) through this callback (AD-8). A trailing space is toggled so an
  // identical consecutive message still triggers a fresh announcement.
  function announce(message: string): void {
    liveMessage = liveMessage === message ? `${message} ` : message;
  }

  // Assertive announcement (Story 4.2): drives the second, assertive live region.
  // A trailing space toggles so an identical consecutive message still re-announces.
  function announceAssertive(message: string): void {
    liveAssertive = liveAssertive === message ? `${message} ` : message;
  }

  function toggleSettings(): void {
    settingsOpen = !settingsOpen;
  }

  // Announce a non-central collision (Story 4.1, Task 7, SC 4.1.3). Diff the set of
  // `collided` body ids across state updates; on a NEW collision, announce a
  // mass-neutral message through the same polite live region the rest of the app
  // uses (never implying mass change or gravitational interaction). The effect reads
  // ONLY `store.state.bodies`, never `liveMessage`, so the announce write cannot
  // re-trigger it. Removal + status behaviour live in state and are identical under
  // reduced motion (AC 7); only the on-canvas explosion (render) degrades.
  let announcedCollided = new Set<BodyId>();
  $effect(() => {
    const bodies = store.state.bodies;
    const current = new Set<BodyId>();
    let hasNew = false;
    for (const b of bodies) {
      if (b.status === 'collided') {
        current.add(b.id);
        if (!announcedCollided.has(b.id)) hasNew = true;
      }
    }
    announcedCollided = current;
    if (hasNew) announce('Two bodies met and were removed from this experiment.');
  });

  // Announce a CENTRAL ABSORPTION (Story 4.2, AC 2/3/6, SC 4.1.3). Diff the set of
  // `absorbed` body ids across state updates; on a NEW absorption, announce a
  // mass-neutral, illustrative message through the ASSERTIVE live region
  // (EXPERIENCE.md L212), naming the body and stating the model does not change the
  // centre's mass or any other orbit. Removal + status live in state and are identical
  // under reduced motion (AC 6); only the on-canvas implosion (render) degrades. The
  // effect reads ONLY `store.state.bodies`, never the live-region message, so the
  // announce write cannot re-trigger it. Announced once per event (detection is
  // idempotent, so an absorbed body is never re-flagged).
  const bodyName = (id: BodyId): string => selectBodyView(store.state, id).name;
  // Collect ALL ids that became `absorbed` in one state update, not just the last
  // one seen in the loop: `liveAssertive` is a single string, so iterating
  // `announceAssertive` per body would collapse to the final call before Svelte
  // flushes the DOM and drop every earlier absorption (Finding 2). Two or more
  // simultaneous absorptions are voiced in ONE combined, count-aware message so no
  // absorption announcement is lost.
  let announcedAbsorbed = new Set<BodyId>();
  $effect(() => {
    const bodies = store.state.bodies;
    const current = new Set<BodyId>();
    const newlyAbsorbed: BodyId[] = [];
    for (const b of bodies) {
      if (b.status === 'absorbed') {
        current.add(b.id);
        if (!announcedAbsorbed.has(b.id)) newlyAbsorbed.push(b.id);
      }
    }
    announcedAbsorbed = current;
    if (newlyAbsorbed.length > 0) {
      const names = newlyAbsorbed.map(bodyName);
      const subject =
        names.length === 1
          ? `${names[0]} reached the centre and was absorbed`
          : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} reached the centre and were absorbed`;
      announceAssertive(
        `${subject}. In this model that is illustrative only: it does not change the centre's mass or any other orbit.`
      );
    }
  });

  // Experimental-mode messaging (Story 2.2, AC 8): read through `state` selectors so
  // `ui` never touches `core`. Reactive, so it appears/updates the moment the centre
  // switches and clears on Reset.
  const isAlternateCentre = $derived(selectIsAlternateCentre(store.state));
  const centreName = $derived(selectCentreName(store.state));

  // Story 5.1 (AC-11): announce the invalid-shared-link notice ONCE on mount through
  // the EXISTING polite live region (SC 4.1.3: announce, never move focus, never a
  // modal), and render the neutral StatusToast below. Runs once; `startupNotice` is
  // a static prop set by the composition root and never changes.
  let announcedStartup = false;
  $effect(() => {
    if (announcedStartup || startupNotice === '') return;
    announcedStartup = true;
    liveMessage = startupNotice;
  });
</script>

<div class="workspace">
  <CanvasHost {store} {resolve} {osReducedMotion} {announce} />

  <TopBar
    {store}
    {announce}
    onToggleSettings={toggleSettings}
    {settingsOpen}
    {buildShareUrl}
    {copyText}
  />

  {#if settingsOpen}
    <SettingsPanel {store} {announce} />
  {/if}

  <button
    type="button"
    class="panels-toggle glass-panel"
    data-testid="panels-toggle"
    aria-expanded={!panelsCollapsed}
    aria-controls="side-panels"
    aria-label={panelsCollapsed ? 'Show side panels' : 'Hide side panels'}
    onclick={togglePanels}
  >
    <span class="material-symbols-outlined" aria-hidden="true">
      {panelsCollapsed ? 'menu' : 'menu_open'}
    </span>
  </button>

  <div id="side-panels" hidden={panelsCollapsed}>
    <BodyLegend {store} {announce} />
    <DetailPanel {store} {announce} />
  </div>

  <PlaybackHud {store} announce={announce} />
  <ModelNotice {isAlternateCentre} {centreName} />
  <StatusToast message={startupNotice} />
  <LiveRegion message={liveMessage} />
  <LiveRegion message={liveAssertive} assertive={true} />
</div>

<style>
  .workspace {
    position: relative;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
  }

  .panels-toggle {
    position: absolute;
    top: calc(var(--top-bar-height) + 12px);
    left: var(--edge-margin);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: var(--radius-full);
    border-top: 1px solid rgba(255, 255, 255, 0.15);
    border-left: 1px solid rgba(255, 255, 255, 0.15);
    color: var(--on-surface);
    cursor: pointer;
    z-index: 12;
  }
</style>
